import React from 'react'
import SharedFilters from '../../../components/SharedFilters'

export default function RemarksStudentPicker({
  students = [],
  selectedIds = [],
  onToggleStudent,
  onToggleAll,
  companyFilter = 'All',
  branchFilter = 'All',
  courseFilter = 'All',
  academicYearFilter = '2026-2027',
  batchFilter = 'All',
  subjectFilter = 'All',
  searchText = '',
  searchField = 'All',
  setCompanyFilter,
  setBranchFilter,
  setCourseFilter,
  setAcademicYearFilter,
  setBatchFilter,
  setSubjectFilter,
  setSearchText,
  setSearchField,
  onReset,
  options = {},
}) {
  const isAllSelected = students.length > 0 && selectedIds.length === students.length

  return (
    <div className="flex flex-col gap-5 rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 sm:p-8 shadow-xs">
      {/* ── Section Title & Selected Count ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/15 pb-4">
        <div className="flex items-center gap-2 text-primary font-bold text-sm">
          <span className="material-symbols-outlined text-base">group_add</span>
          <span>Target Students Selection ({students.length})</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-xl bg-primary/10 px-3 py-1 text-xs font-black text-primary">
            <span className="material-symbols-outlined text-sm">check_circle</span>
            <span>{selectedIds.length} Selected</span>
          </span>

          <button
            type="button"
            onClick={onToggleAll}
            className="rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-1 text-xs font-bold text-on-surface hover:bg-surface-container transition cursor-pointer"
          >
            {isAllSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>
      </div>

      {/* ── Shared Filters for Student Selection ── */}
      <SharedFilters
        searchField={searchField}
        searchText={searchText}
        companyFilter={companyFilter}
        branchFilter={branchFilter}
        courseFilter={courseFilter}
        academicYearFilter={academicYearFilter}
        batchFilter={batchFilter}
        subjectFilter={subjectFilter}
        setSearchField={setSearchField}
        setSearchText={setSearchText}
        setCompanyFilter={setCompanyFilter}
        setBranchFilter={setBranchFilter}
        setCourseFilter={setCourseFilter}
        setAcademicYearFilter={setAcademicYearFilter}
        setBatchFilter={setBatchFilter}
        setSubjectFilter={setSubjectFilter}
        onReset={onReset}
        onSearch={() => {}}
        searchFieldOptions={options.searchFields}
        companyOptions={options.companies}
        branchOptions={options.branches}
        courseOptions={options.courses}
        batchOptions={options.batches}
        subjectOptions={options.subjects}
        academicYearOptions={options.academicYears}
        visibleAccordionFilters={[
          'company',
          'branch',
          'course',
          'academicYear',
          'batch',
          'subject',
        ]}
      />

      {/* ── Students Selection Table ── */}
      <div className="overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-low/30">
        <div className="max-h-[380px] overflow-y-auto custom-scrollbar">
          <table className="w-full text-left text-xs text-on-surface border-collapse">
            <thead className="sticky top-0 z-10 border-b border-outline-variant/25 bg-surface-container-low text-[10px] font-black uppercase tracking-wider text-on-surface-variant">
              <tr>
                <th className="w-12 px-3 py-2.5 text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={onToggleAll}
                    className="h-4 w-4 rounded accent-primary cursor-pointer"
                  />
                </th>
                <th className="w-20 px-3 py-2.5 text-center">Roll No</th>
                <th className="px-3 py-2.5">Student Name</th>
                <th className="px-3 py-2.5">Course &amp; Batch</th>
                <th className="px-3 py-2.5">Branch / Company</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-outline-variant/15 font-medium">
              {students.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-xs text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <span className="material-symbols-outlined text-3xl text-outline-variant">
                        person_search
                      </span>
                      <p className="font-bold text-on-surface">No Students Found</p>
                      <p className="text-[11px]">Adjust your filter selection to view matching students.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                students.map((student) => {
                  const isChecked = selectedIds.includes(student.id)
                  return (
                    <tr
                      key={student.id}
                      onClick={() => onToggleStudent(student.id)}
                      className={`cursor-pointer transition-colors ${
                        isChecked
                          ? 'bg-primary/5 hover:bg-primary/10'
                          : 'hover:bg-surface-container-low'
                      }`}
                    >
                      <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => onToggleStudent(student.id)}
                          className="h-4 w-4 rounded accent-primary cursor-pointer"
                        />
                      </td>

                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex items-center justify-center rounded-lg bg-surface-container-high px-2 py-0.5 text-xs font-black text-on-surface">
                          {student.rollNo}
                        </span>
                      </td>

                      <td className="px-3 py-2">
                        <span className="font-display text-xs font-extrabold text-[#3b82f6]">
                          {student.fullName}
                        </span>
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <span className="font-semibold text-primary">{student.courseName}</span>
                          <span>•</span>
                          <span className="text-on-surface-variant">{student.batchNames}</span>
                        </div>
                      </td>

                      <td className="px-3 py-2 text-[11px] text-on-surface-variant">
                        <span className="font-semibold text-on-surface">{student.branchName}</span>
                        <span className="text-[10px] text-on-surface-variant ml-1">
                          ({student.instituteName})
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
